import React, { memo } from 'react';
import { Trans } from 'react-i18next';

import { CheckoutConfirmationTable } from 'src/pages/Checkout/components/common/Table';
import { CheckoutConfirmationResponseType } from 'src/models/Checkout/Confirmation';
import { UiOuterLink } from 'src/components/UI/OuterLink';
import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';
import { UiMereLabel } from 'src/components/UI/UiMereLabel';
import { UiCard } from 'src/components/UI/Card';
import { ROUTE_MY_ACCOUNT_PAGE } from 'src/routes';
import { useTHelper } from 'src/utils/i18n';

type Props = {
  model: CheckoutConfirmationResponseType['FundraisingPageSubsection'];
};

export const CheckoutConfirmationFundraising = memo<Props>(({ model }) => {
  const locale = 'checkout.confirmation.fundraising';
  const tHelper = useTHelper(locale);

  const teamMemberSingular = model.TeamMemberTerminology.Singular?.toLowerCase() || '';
  const teamSingular = model.TeamTerminology.Singular?.toLowerCase() || '';

  let singular = '';

  if (model.HasTeamMemberRegistrations && model.HasTeamRegistrations) {
    singular = tHelper('teamAndTeamMember', { teamMemberSingular, teamSingular });
  } else if (model.HasTeamMemberRegistrations) {
    singular = teamMemberSingular || '';
  } else if (model.HasTeamRegistrations) {
    singular = teamSingular || '';
  }

  const supporters = model.SupporterTerminology.Plural?.toLowerCase() || '';

  const getColumns = (singular: string) => [
    { name: tHelper('table.name', { singular }), className: 'left' },
    { name: tHelper('table.fundraisingPage'), className: 'left' },
  ];

  return (
    <>
      {model.IsFundraisingPagesEnabled && (
        <>
          <h2 className="section-header">{tHelper('title')}</h2>
          <UiCard>
            <p className="no-margin">
              <UiMereLabel className="text-left">
                {tHelper('customize', { plural: model.DonationTerminology.Plural?.toLowerCase() || '' })}
                <br />
                {tHelper('clickToStart')}
              </UiMereLabel>
            </p>
          </UiCard>

          {model.HasTeamRegistrations && (
            <CheckoutConfirmationTable
              columns={getColumns(model.TeamTerminology.Singular || '')}
              data={model.SelectedTeamRegistrations}
            >
              {(item) => (
                <>
                  <td className="left">{item.Name}</td>
                  <td className="left">
                    <UiOuterLink href={item.FundraisingPageUrl} className="link-hoverable">
                      {item.FundraisingPageUrl}
                    </UiOuterLink>
                  </td>
                </>
              )}
            </CheckoutConfirmationTable>
          )}

          {model.HasTeamMemberRegistrations && (
            <CheckoutConfirmationTable
              columns={getColumns(model.TeamMemberTerminology.Singular || '')}
              data={model.SelectedTeamMemberRegistrations}
            >
              {(item) => (
                <>
                  <td className="left">{item.Name}</td>
                  <td className="left">
                    <UiOuterLink href={item.FundraisingPageUrl} className="link-hoverable">
                      {item.FundraisingPageUrl}
                    </UiOuterLink>
                  </td>
                </>
              )}
            </CheckoutConfirmationTable>
          )}
        </>
      )}

      <UiCard>
        <p className="no-margin">
          <UiMereLabel className="text-left">
            <Trans i18nKey={`${locale}.goToAccount`} values={{ singular }}>
              <UiInnerNotSPALink className="link-hoverable" href={ROUTE_MY_ACCOUNT_PAGE}>
                1
              </UiInnerNotSPALink>
            </Trans>

            <br />
            <br />
            {model.IsCampaignPledge && model.HasTeamMemberRegistrations
              ? tHelper('personalized.team', { supporters })
              : tHelper('personalized.teamMember', { supporters })}
          </UiMereLabel>
        </p>
      </UiCard>
    </>
  );
});
